/**
 * Sizes the product media frame to the slide that is currently showing.
 *
 * With aspect_ratio "adapt" the gallery frame ends up as tall as the tallest
 * media in the product, because the slides are flex items in one row. A
 * portrait video next to square photos therefore leaves a band of page
 * background under every other slide — on mobile that reads as a gap between
 * the image and the thumbnail rail.
 *
 * Each slide already carries its own ratio as --ratio on .product-media, so
 * mirror the active one onto the gallery as --gallery-frame-ratio and let CSS
 * apply it to the frame. Ratios are clamped so a freak asset can't blow up the
 * column (taller than 1:2) or squash it to a sliver (wider than 3:1); media
 * outside that range letterboxes, which is what media_fit 'contain' already
 * does everywhere else.
 *
 * Liquid seeds --gallery-frame-ratio from the first media, so the first paint
 * is already correct and this only has to keep up with slide changes.
 *
 * --gallery-rail-height is the second half: on desktop the thumbnails are a
 * column sized to the frame, so senselab.css floors the frame at the rail's
 * own height to stop a wide slide squashing the thumbnails. The rail's height
 * comes from its content, not from the frame, so measuring it here can't feed
 * back into the layout.
 */

const MIN_RATIO = 0.5;
const MAX_RATIO = 3;

/**
 * Reads the clamped aspect ratio of a slide's media.
 * @param {Element | null | undefined} slide
 * @returns {number | null} ratio, or null when the slide has no usable one
 */
function ratioOf(slide) {
  const media = slide?.querySelector?.('.product-media');
  if (!(media instanceof HTMLElement)) return null;

  const ratio = parseFloat(media.style.getPropertyValue('--ratio'));
  if (!Number.isFinite(ratio) || ratio <= 0) return null;

  return Math.min(Math.max(ratio, MIN_RATIO), MAX_RATIO);
}

/**
 * @param {HTMLElement} gallery
 * @param {Element | null | undefined} slide
 */
function sizeFrameToSlide(gallery, slide) {
  const ratio = ratioOf(slide);
  if (ratio == null) return;

  gallery.style.setProperty('--gallery-frame-ratio', String(ratio));
}

/**
 * @param {HTMLElement} gallery
 * @param {Element} rail
 */
function measureThumbnailRail(gallery, rail) {
  const height = `${rail.scrollHeight}px`;
  if (gallery.style.getPropertyValue('--gallery-rail-height') === height) return;

  gallery.style.setProperty('--gallery-rail-height', height);
}

// The rail is a column beside the frame on desktop and a row under it on
// mobile, so its height has to be re-read when the breakpoint changes.
const railObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const gallery = entry.target.closest('media-gallery');
    if (gallery instanceof HTMLElement) measureThumbnailRail(gallery, entry.target);
  }
});

/** @param {ParentNode} root */
function setUpGalleries(root) {
  for (const gallery of root.querySelectorAll('media-gallery')) {
    if (!(gallery instanceof HTMLElement)) continue;

    const rail = gallery.querySelector('.slideshow-controls__thumbnails');
    if (rail) {
      measureThumbnailRail(gallery, rail);
      railObserver.observe(rail);
    }

    // Liquid seeds the ratio; only fall back if it didn't (older markup).
    if (gallery.style.getPropertyValue('--gallery-frame-ratio')) continue;
    sizeFrameToSlide(gallery, gallery.querySelector('slideshow-slide'));
  }
}

document.addEventListener('slideshow:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const gallery = target.closest('media-gallery');
  if (!(gallery instanceof HTMLElement)) return;

  sizeFrameToSlide(gallery, /** @type {CustomEvent} */ (event).detail?.slide);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) setUpGalleries(event.target);
});

// Picking a variant swaps in freshly rendered gallery markup, which carries
// Liquid's seeded ratio but not the measured rail, and whose rail is a new
// element the observer above isn't watching yet.
function watchForReplacedGalleries() {
  new MutationObserver((records) => {
    const replaced = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) => node instanceof Element && (node.matches('media-gallery') || node.querySelector('media-gallery'))
      )
    );

    if (replaced) setUpGalleries(document);
  }).observe(document.body, { childList: true, subtree: true });
}

function init() {
  setUpGalleries(document);
  watchForReplacedGalleries();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
